'use client'

import { cn } from '@/lib/utils'
import {
  Base,
  Button,
  Container,
  Img,
  LangSwitch,
  Richtext
} from '@/ui/components'
import Link from 'next/link'
import { memo, useEffect } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import GuuruButton from '../GuuruButton'
import Menu from './Components/Menu'
import Newsletter from './Components/Newsletter'
import Socials from './Components/Socials'
import FooterProps from './type'

const Footer = ({ data, dataIntegrations, ...props }: FooterProps) => {
  const CONTACTS = data?.contacts
  const NAV = data?.navigation
  const NEWSLETTER = data?.newsletter
  const PARTNERS = data?.partners?.length ? data?.partners[0] : null

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1
  })

  useEffect(() => {
    if (isIntersecting) {
      document.body.dataset.footerVisible = 'true'
    } else {
      document.body.dataset.footerVisible = 'false'
    }
  }, [isIntersecting])

  return (
    <Base as="footer" id="footer" ref={ref as any}>
      <div className="bg-primary-200">
        <Container
          className={cn(
            'py-5xl lg:pb-4xl gap-xl md:gap-gutter grid grid-cols-1 lg:grid-cols-2'
          )}
          data-transition={props.transition}
        >
          <Newsletter data={NEWSLETTER} />

          <div className="gap-xl md:gap-gutter flex flex-col md:grid md:grid-cols-2">
            <div className="gap-md flex flex-col">
              <h4 className="textstyle-md-bold text-primary-300/50">
                {CONTACTS?.title}
              </h4>
              {CONTACTS?.items?.map((item, index) => (
                <div
                  key={index?.toString()}
                  className="gap-md flex flex-col text-neutral-600"
                >
                  <div>
                    <p className="textstyle-md-bold">{item?.name}</p>
                    {item?.address && <Richtext as="p" text={item.address} />}
                    <p>{item?.city}</p>
                  </div>
                  <div>
                    {item?.email && (
                      <div>
                        <Button
                          href={`mailto:${item.email}`}
                          variant="text"
                          size="m"
                          icon="material-symbols:mail-sharp"
                          iconPosition="left"
                        >
                          {item.email}
                        </Button>
                      </div>
                    )}
                    {item?.tel && (
                      <div>
                        <Button
                          size="m"
                          href={`tel:${item.tel}`}
                          variant="text"
                          icon="material-symbols:phone-enabled-sharp"
                          iconPosition="left"
                        >
                          {item.tel}
                        </Button>
                      </div>
                    )}
                  </div>
                  {index === 0 &&
                    dataIntegrations?.guruChat?.categoryId &&
                    dataIntegrations?.guruChat?.appId && <GuuruButton />}
                </div>
              ))}
              {data?.socialLinks && <Socials data={data?.socialLinks} />}
            </div>
            <div className="">
              {NAV?.infos && <Menu data={NAV?.infos} />}
              {NAV?.partners && <Menu data={NAV?.partners} />}
              {NAV?.company && <Menu data={NAV?.company} />}
            </div>
          </div>
        </Container>
      </div>
      {PARTNERS && (
        <Container className="py-xl">
          <h3 className="textstyle-md-bold text-primary-300/80 mb-md">
            {PARTNERS.title}
          </h3>
          {!!PARTNERS.items?.length && (
            <div
              className={cn(
                'gap-2xl flex flex-wrap items-center justify-start'
              )}
              data-transition={props.transition}
            >
              {PARTNERS.items.map(
                (partner) =>
                  partner?.link?.href &&
                  partner?.image && (
                    <div key={partner.id}>
                      <Link
                        href={partner.link.href}
                        target={partner.link.target || undefined}
                        rel={
                          partner.link.target === '_blank'
                            ? 'noopener noreferrer'
                            : partner.link.additionalAttributes?.rel ||
                              undefined
                        }
                        title={partner.link.title || undefined}
                      >
                        {partner.image && (
                          <Img
                            data={partner.image}
                            naked
                            className="h-20 w-auto object-contain md:h-[100px]"
                          />
                        )}
                      </Link>
                    </div>
                  )
              )}
            </div>
          )}
        </Container>
      )}
      <div className="bg-primary-300">
        <Container className="gap-gutter py-lg textstyle-sm-regular flex items-start justify-between text-white md:items-center">
          <div>
            {data?.copyright && (
              <p dangerouslySetInnerHTML={{ __html: data.copyright }} />
            )}
          </div>
          <div>
            <LangSwitch mode="invert" />
          </div>
        </Container>
      </div>
    </Base>
  )
}

export default memo(Footer)
